import { Button, ICON_COLOR } from '@zep-us/zus';
import { BoltFillIcon, ChartLineIcon, PenPaperIcon, RectanglePortraitStackFillIcon, SquareStackIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { NewChip } from '@/shared/components/Chip/NewChip';
import { CTAButtonRightSlot } from './CTAButtonRightSlot';
import { WorksheetGradientIcon } from './WorksheetGradientIcon';

/**
 * OwnerLockedCTAButtons Props
 */
export interface OwnerLockedCTAButtonsProps {
  /** 잠금된 퀴즈 복사 핸들러 */
  onDuplicateLocked: () => void;
  /** 플랜 업그레이드 핸들러 */
  onUpgradePlan: () => void;
  /** 학습 리포트 핸들러 */
  onGoToLms: () => void;
  /** 수정하기 핸들러 */
  onEdit: () => void;
  /** 워크시트 내보내기 핸들러 */
  onExportWorksheet: () => void;
  /** 플래시카드 학습 진입 핸들러 */
  onFlashcard: () => void;
}

/**
 * OwnerLockedCTAButtons - 소유자용 잠금된 퀴즈 CTA 버튼 그룹
 *
 * @description
 * 플랜 만료로 인해 입장이 제한된 퀴즈의 소유자에게 보여지는 CTA 버튼입니다.
 * 퀴즈를 복사하거나 플랜을 업그레이드할 수 있도록 안내합니다.
 *
 * Story 2.6: 소유자용 잠금된 퀴즈 CTA
 *
 * @example
 * ```tsx
 * <OwnerLockedCTAButtons
 *   onDuplicateLocked={handleDuplicateLocked}
 *   onUpgradePlan={handleUpgradePlan}
 *   onGoToLms={handleGoToLms}
 * />
 * ```
 */
export const OwnerLockedCTAButtons = ({
  onDuplicateLocked,
  onUpgradePlan,
  onGoToLms,
  onEdit,
  onExportWorksheet,
  onFlashcard
}: OwnerLockedCTAButtonsProps) => {
  const {
    t
  } = useTranslation(['common']);
  const buttonsStyle = 'group w-full justify-start px-md gap-xs';
  return <div className="flex w-full flex-col gap-md" data-sentry-component="OwnerLockedCTAButtons" data-sentry-source-file="OwnerLockedCTAButtons.tsx">
      {/* CTA 버튼 그룹 */}
      <div className="flex w-full flex-col gap-xs">
        {/* 플랜 결제 */}
        <Button size="md" variant="contained" intent="gradient" onClick={onUpgradePlan} className={buttonsStyle} leftSlot={<BoltFillIcon size="sm" fill={ICON_COLOR.inverse} />} rightSlot={<CTAButtonRightSlot />} data-sentry-element="Button" data-sentry-source-file="OwnerLockedCTAButtons.tsx">
          {t('detail.payPlan')}
        </Button>
        {/* 2. 플래시카드 - Flashcard button */}
        <Button size="md" variant="outlined" intent="neutral" onClick={onFlashcard} leftSlot={<RectanglePortraitStackFillIcon size="sm" className="text-blue-300" />} rightSlot={<CTAButtonRightSlot badge={<NewChip className="bg-blue-50 text-blue-500" />} />} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="OwnerLockedCTAButtons.tsx">
          {t('common:flashcard.label')}
        </Button>
        {/* 3. 워크시트 - Worksheet button */}
        <Button size="md" variant="outlined" intent="neutral" onClick={onExportWorksheet} leftSlot={<WorksheetGradientIcon size="sm" />} rightSlot={<CTAButtonRightSlot />} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="OwnerLockedCTAButtons.tsx">
          {t('common:quizWorksheet.label')}
        </Button>
        <div className="flex w-full flex-col gap-xs pt-sm">
          <p className="text-caption-lg-semibold text-alternative">
            {t('common:detail.quizManagement')}
          </p>
          {/* 학습 리포트 */}
          <Button size="md" variant="contained" intent="alternative" onClick={onGoToLms} className={buttonsStyle} leftSlot={<ChartLineIcon size="sm" fill={ICON_COLOR.strong} />} rightSlot={<CTAButtonRightSlot />} data-sentry-element="Button" data-sentry-source-file="OwnerLockedCTAButtons.tsx">
            {t('detail.learningReport')}
          </Button>
          {/* 복사하기 */}
          <Button size="md" intent="neutral" variant="outlined" leftSlot={<SquareStackIcon size="sm" fill={ICON_COLOR.neutral} />} rightSlot={<CTAButtonRightSlot />} onClick={onDuplicateLocked} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="OwnerLockedCTAButtons.tsx">
            {t('detail.copy')}
          </Button>
          {/* 수정하기 */}
          <Button size="md" intent="neutral" variant="outlined" leftSlot={<PenPaperIcon size="sm" fill={ICON_COLOR.neutral} />} rightSlot={<CTAButtonRightSlot />} onClick={onEdit} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="OwnerLockedCTAButtons.tsx">
            {t('detail.edit')}
          </Button>
          <p className="text-caption-lg-semibold text-assistive">
            {t('detail.planExpired.ownerMessage')}
          </p>
        </div>
      </div>
    </div>;
};